/**
 * Social API Client (Client-side)
 * Calls server-side /api/social/* proxy routes
 */

import { api } from "./apteva";

export interface SocialAccount {
  id: number;
  uuid: string;
  platform: string;
  platform_account_id: string;
  platform_username: string;
  platform_display_name: string;
  platform_avatar_url: string;
  platform_metadata: Record<string, any>;
  status: string;
  status_message: string;
  last_used_at: string;
  created_at: string;
}

export interface SocialPost {
  id: number;
  uuid: string;
  text: string;
  media_urls: { url: string; type: string; alt_text?: string }[];
  link_url: string;
  platform_overrides: Record<string, any>;
  account_ids: number[];
  platform_results: Record<string, PlatformResult>;
  status: string;
  total_accounts: number;
  successful_count: number;
  failed_count: number;
  published_at: string;
  created_at: string;
  scheduled_for?: string;
  timezone?: string;
  job_id?: string;
}

export interface PlatformResult {
  platform: string;
  account_name?: string;
  status: string;
  platform_post_id?: string;
  platform_post_url?: string;
  error?: string;
  published_at?: string;
}

export interface PlatformAnalytics {
  platform_post_id?: string;
  platform_post_url?: string;
  account_name?: string;
  metrics: Record<string, number> | null;
  reason?: string;
}

export interface PostAnalytics {
  post_id: number;
  text?: string;
  status: string;
  published_at?: string;
  analytics: Record<string, PlatformAnalytics>;
  totals: Record<string, number>;
  history?: Record<string, Record<string, { value: number; timestamp: string }[]>>;
}

export interface InsightMetric {
  title: string;
  description: string;
  period: string;
  total_value?: { value: number } | null;
  values: { value: number; end_time: string }[];
}

export interface AccountInsights {
  platform: string;
  account_name: string;
  page_id?: string;
  account_id?: string;
  current: Record<string, number | null>;
  metrics: Record<string, InsightMetric>;
  monetization?: {
    enrolled: boolean;
    total: number | null;
    currency: string | null;
    breakdown: Record<string, number> | null;
    period?: { since: number; until: number };
  } | null;
  paging?: any;
}

export interface HistoryMetric {
  metric_name: string;
  data_points: number;
  values: { value: number; timestamp: string }[];
  latest: number;
  first: number;
  change_pct?: number;
  change_abs: number;
}

export interface AccountHistory {
  account_id: number;
  platform: string;
  account_name: string;
  time_range: { start: string; end: string };
  granularity: string;
  metrics: Record<string, HistoryMetric>;
}

export interface FacebookPage {
  id: string;
  name: string;
  category: string;
  fan_count: number;
  picture_url: string;
  has_instagram: boolean;
  instagram_account: InstagramAccount | null;
}

export interface InstagramAccount {
  id: string;
  username: string;
  name: string;
  profile_picture_url: string;
  facebook_page_id?: string;
  facebook_page_name?: string;
}

export interface InboxItem {
  id: number;
  social_account_id: number;
  project_id: number;
  platform: string;
  type: "comment" | "reply" | "message" | "story_reply";
  post_id: string;
  comment_id: string;
  parent_comment_id: string | null;
  from_id: string;
  from_name: string;
  message: string;
  is_own: boolean;
  status: "unread" | "read" | "replied" | "archived" | "deleted";
  platform_created_at: string;
  created_at: string;
  account?: {
    id: number;
    platform: string;
    platform_display_name: string;
    platform_avatar_url: string;
    platform_account_id: string;
  };
}

export interface InboxComment {
  id: string;
  message: string;
  from_id: string;
  from_name: string;
  created_time: string;
  like_count: number;
  reply_count: number;
  attachment?: any;
  is_own: boolean;
  canReply: boolean;
  canDelete: boolean;
  canHide: boolean;
  platform: string;
  replies?: InboxComment[];
}

export const social = {
  // Accounts
  listAccounts: (params?: Record<string, any>) =>
    api.get<{ success: boolean; data: SocialAccount[]; total: number }>("/social/accounts", params),

  getAccount: (id: number) =>
    api.get<{ success: boolean; data: SocialAccount }>(`/social/accounts/${id}`),

  connectAccount: (data: { platform: string }) =>
    api.post<{ success: boolean; data: { auth_url?: string; id: number; pending_account_id: number; platform: string } }>("/social/accounts/connect", data),

  disconnectAccount: (id: number) =>
    api.delete<{ success: boolean }>(`/social/accounts/${id}`),

  reconnectAccount: (id: number) =>
    api.post<{ success: boolean; data: { auth_url?: string; id: number; pending_account_id: number; platform: string } }>(`/social/accounts/${id}/reconnect`, {}),

  listPages: (id: number) =>
    api.get<{ success: boolean; data: { pages: FacebookPage[]; instagram_accounts: InstagramAccount[]; requires_picker: boolean; platform: string } }>(`/social/accounts/${id}/pages`),

  selectPage: (id: number, data: { page_id?: string; instagram_account_id?: string }) =>
    api.post<{ success: boolean; data: any }>(`/social/accounts/${id}/select-page`, data),

  // Posts
  listPosts: (params?: Record<string, any>) =>
    api.get<{ success: boolean; data: SocialPost[]; total: number }>("/social/posts", params),

  getPost: (id: number) =>
    api.get<{ success: boolean; data: SocialPost }>(`/social/posts/${id}`),

  createPost: (data: { mode?: "draft" | "publish"; text?: string; media_storage_ids?: number[]; link_url?: string; account_ids: number[]; platform_overrides?: Record<string, any>; scheduled_for?: string; timezone?: string }) =>
    api.post<{ success: boolean; data: SocialPost }>("/social/posts", data),

  syncPinterestPosts: () =>
    api.post<{ success: boolean; data: { imported: number; skipped_existing: number; failed: number } }>("/social/posts/sync", {}),

  cancelScheduledPost: (id: number) =>
    api.post<{ success: boolean; data: any }>(`/social/posts/${id}/cancel`, {}),

  reschedulePost: (id: number, data: { scheduled_for: string; timezone?: string }) =>
    api.post<{ success: boolean; data: any }>(`/social/posts/${id}/reschedule`, data),

  updatePost: (id: number, data: { text: string; platform_overrides?: Record<string, any> }) =>
    api.put<{ success: boolean; data: any }>(`/social/posts/${id}`, data),

  deletePost: (id: number) =>
    api.delete<{ success: boolean; data: any }>(`/social/posts/${id}`),

  retryPost: (id: number) =>
    api.post<{ success: boolean; data: any }>(`/social/posts/${id}/retry`, {}),

  getPostAnalytics: (id: number) =>
    api.get<{ success: boolean; data: PostAnalytics }>(`/social/posts/${id}/analytics`),

  // Insights
  getAccountInsights: (id: number | string, params?: Record<string, any>) =>
    api.get<{ success: boolean; data: AccountInsights }>(`/social/accounts/${id}/insights`, params),

  // History (stored snapshots from metrics_data)
  getAccountHistory: (id: number | string, params?: Record<string, any>) =>
    api.get<{ success: boolean; data: AccountHistory }>(`/social/accounts/${id}/history`, params),

  // Inbox
  listInbox: (params?: Record<string, any>) =>
    api.get<{ success: boolean; data: InboxItem[]; total: number; unread_count: number }>("/social/inbox", params),

  getInboxComments: (params: { social_account_id: number; post_id?: number; platform_post_id?: string; cursor?: string }) =>
    api.get<{ success: boolean; data: { comments: InboxComment[]; paging: any; post_id: string } }>("/social/inbox/comments", params as any),

  replyInbox: (data: { social_account_id: number; message: string; comment_id?: string; post_id?: string; platform_post_id?: string; inbox_item_id?: number; private_reply?: boolean }) =>
    api.post<{ success: boolean; data: { reply_id: string; platform: string; message: string } }>("/social/inbox/reply", data),

  updateInbox: (data: { id?: number; ids?: number[]; status?: string; mark_all_read?: boolean; social_account_id?: number }) =>
    api.put<{ success: boolean; data: { updated: number } }>("/social/inbox", data),

  deleteInboxComment: (params: { social_account_id: number; comment_id: string; inbox_item_id?: number }) =>
    api.delete<{ success: boolean }>(`/social/inbox/comment?social_account_id=${params.social_account_id}&comment_id=${params.comment_id}${params.inbox_item_id ? `&inbox_item_id=${params.inbox_item_id}` : ""}`),
};

export const PLATFORMS = [
  { id: "pinterest", name: "Pinterest", icon: "P", color: "bg-red-700" },
] as const;

export function getPlatformInfo(platformId: string) {
  return PLATFORMS.find(p => p.id === platformId) || { id: platformId, name: platformId, icon: "?", color: "bg-gray-500" };
}
